import { BaseHttpClient } from "../../http/base-http-client";
import type {
  TokenResponse,
  LoginRequest,
  RegisterRequest,
  VerifyEmailRequest,
  ResetPasswordRequest,
  ResendVerificationEmailRequest,
  PublicConfigResponse,
} from "../../types/api/auth-types";
import type { User } from "../../types/api/user-types";
import type { MessageResponse } from "../../types/api/common-types";

/**
 * Authentication API Client
 *
 * Handles all authentication-related endpoints:
 * - Basic auth flows (login, register, verify email, password recovery)
 * - Organisation-first login (methods + org-scoped OIDC, Phase K1)
 * - Public configuration retrieval
 *
 * Follows SRP: Single responsibility for authentication operations
 * Follows SOC: Separated from business logic and UI concerns
 */
export class AuthApiClient extends BaseHttpClient {
  /**
   * Authenticate user with email and password
   * @param data - Login credentials
   * @returns Promise<TokenResponse> - Access token and related information
   */
  async login(data: LoginRequest): Promise<TokenResponse> {
    // Convert to OAuth2PasswordRequestForm format (URL-encoded form data)
    const formData = new URLSearchParams();
    formData.append("username", data.email); // OAuth2 uses 'username' field for email
    formData.append("password", data.password);
    formData.append("grant_type", "password");

    const response = await this.axiosInstance.post<TokenResponse>(
      "/auth/login/access-token",
      formData.toString(),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
    return response.data;
  }

  /**
   * Register a new user account
   * @param data - Registration information including email, password, and optional details
   * @returns Promise<User> - Created user information
   */
  async register(data: RegisterRequest): Promise<User> {
    return this.post<User>("auth/signup", data);
  }

  /**
   * Verify user email address with token
   * @param data - Email verification token
   * @returns Promise<MessageResponse> - Verification confirmation message
   */
  async verifyEmail(data: VerifyEmailRequest): Promise<MessageResponse> {
    return this.post<MessageResponse>("auth/verify-email", data);
  }

  /**
   * Request password recovery email for user
   * @param email - User's email address
   * @returns Promise<MessageResponse> - Confirmation that recovery email was sent
   */
  async requestPasswordRecovery(email: string): Promise<MessageResponse> {
    return this.post<MessageResponse>(`auth/password-recovery/${email}`, {});
  }

  /**
   * Reset user password with recovery token
   * @param data - Password reset token and new password
   * @returns Promise<MessageResponse> - Confirmation that password was reset
   */
  async resetPassword(data: ResetPasswordRequest): Promise<MessageResponse> {
    return this.post<MessageResponse>("auth/reset-password", data);
  }

  /**
   * Resend email verification for user
   * @param data - User's email address
   * @returns Promise<MessageResponse> - Confirmation that verification email was resent
   */
  async resendVerificationEmail(
    data: ResendVerificationEmailRequest
  ): Promise<MessageResponse> {
    return this.post<MessageResponse>("auth/resend-verification-email", data);
  }

  /**
   * Get public configuration for client-side authentication setup
   * @returns Promise<PublicConfigResponse> - Public configuration including feature flags
   */
  async getPublicConfig(): Promise<PublicConfigResponse> {
    return this.get<PublicConfigResponse>("public-config");
  }

  /**
   * Organisation-first login (Phase K1): the enabled authentication methods
   * of ONE organisation, resolved by slug. The login page must render only
   * these.
   */
  async getOrganisationLoginMethods(
    slug: string
  ): Promise<OrganisationLoginMethods> {
    return this.get<OrganisationLoginMethods>(
      `auth/organizations/${encodeURIComponent(slug)}/methods`
    );
  }

  /**
   * Begin an organisation-scoped OIDC login: returns the identity
   * provider's authorization URL (with a server-signed state).
   */
  async getOidcAuthorizeUrl(
    providerId: string,
    redirectUri: string
  ): Promise<{ authorization_url: string; state: string }> {
    return this.get<{ authorization_url: string; state: string }>(
      `auth/oidc/${providerId}/authorize-url`,
      { redirect_uri: redirectUri }
    );
  }

  /**
   * Complete an organisation-scoped OIDC login: exchanges the authorization
   * code for NEO tokens (id_token verification happens server-side).
   */
  async oidcExchange(data: {
    code: string;
    state: string;
    redirect_uri: string;
  }): Promise<TokenResponse> {
    return this.post<TokenResponse>("auth/oidc/exchange", data);
  }
}

export interface OrganisationLoginMethod {
  type: "local" | "oidc";
  provider_id: string | null;
  display_name: string | null;
}

export interface OrganisationLoginMethods {
  organization: { name: string; slug: string };
  methods: OrganisationLoginMethod[];
}

/**
 * Create a default AuthApiClient instance
 * @returns AuthApiClient - Configured auth API client
 */
export function createAuthApiClient(): AuthApiClient {
  return new AuthApiClient();
}

/**
 * Default AuthApiClient instance for immediate use
 */
export const authApiClient = createAuthApiClient();
